import { useState } from 'react';
import { Button, TooltipProvider } from '@design-system';
import { useProjectionForPolicy, usePolicyById } from '../hooks/useProjectionForPolicy';
import { useIsWizardSession } from '../hooks/useIsWizardSession';
import { PolicyDetailsCard } from './PolicyDetailsCard';
import { ReturnsSummaryCard } from './ReturnsSummaryCard';
import { MonthlyBreakdownTable } from './MonthlyBreakdownTable';
import { IdentityChecksPanel } from './IdentityChecksPanel';
import { TranscriptDialog } from './TranscriptDialog';
import { EmptyState } from './EmptyState';
import { EngineDiffDialog } from './EngineDiffDialog';

interface ProjectionDetailsContentProps {
  fundingSourceId: string | undefined;
  showEngineTools?: boolean;
}

/**
 * The body of the projection-details view: the wizard tools row plus the
 * policy/returns/monthly/identity cards. Shared by `ProjectionDetailsPage`
 * (full page, with a policy picker) and the funding-table slide-out.
 */
export function ProjectionDetailsContent({
  fundingSourceId,
  showEngineTools = true,
}: ProjectionDetailsContentProps) {
  const projection = useProjectionForPolicy(fundingSourceId);
  const policy = usePolicyById(fundingSourceId);
  const isWizard = useIsWizardSession();
  const [engineDiffOpen, setEngineDiffOpen] = useState(false);
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {showEngineTools && isWizard && (
          <div className="flex items-center justify-end gap-3">
            <Button variant="outline" size="sm" onClick={() => setEngineDiffOpen(true)}>
              Compare engines
            </Button>
            {projection && (
              <Button variant="outline" size="sm" onClick={() => setTranscriptOpen(true)}>
                View as text
              </Button>
            )}
          </div>
        )}

        {showEngineTools && isWizard && (
          <EngineDiffDialog
            fundingSourceId={fundingSourceId}
            open={engineDiffOpen}
            onClose={() => setEngineDiffOpen(false)}
          />
        )}

        {projection && (
          <TranscriptDialog
            projection={projection}
            open={transcriptOpen}
            onClose={() => setTranscriptOpen(false)}
          />
        )}

        {!projection ? (
          <EmptyState
            title="No projection data yet"
            body="Run a calculation on this client to populate the projection details."
          />
        ) : (
          <div className="space-y-3">
            <PolicyDetailsCard policy={policy} />
            <ReturnsSummaryCard projection={projection} />
            <MonthlyBreakdownTable projection={projection} policyType={policy?.policyType} />
            <IdentityChecksPanel projection={projection} />
          </div>
        )}
      </div>
    </TooltipProvider>
  );
}
